import React, { Suspense, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import Header from './components/Header';
import Footer from './components/Footer';
import { trackAIVisit, pageview } from './utils/analytics';
import { detectLangFromPath } from './utils/locale';

const HomePage = React.lazy(() => import('./pages/HomePage'));
const ProjectPage = React.lazy(() => import('./pages/ProjectPage'));
const ProfilePage = React.lazy(() => import('./pages/ProfilePage'));
const SupportPage = React.lazy(() => import('./pages/SupportPage'));
const ContactPage = React.lazy(() => import('./pages/ContactPage'));
const RacePage = React.lazy(() => import('./pages/RacePage'));
const EquipmentsPage = React.lazy(() => import('./pages/EquipmentsPage'));
const Links = React.lazy(() => import('./pages/Links'));
const AppLandingPage = React.lazy(() => import('./pages/AppLandingPage'));
const Race2026Page = React.lazy(() => import('./pages/Race2026Page'));
const EquipmentGuidePage = React.lazy(() => import('./pages/EquipmentGuidePage'));
const PhotosPage = React.lazy(() => import('./pages/PhotosPage'));
const CostPage = React.lazy(() => import('./pages/CostPage'));
const KilimanjaroPage = React.lazy(() => import('./pages/KilimanjaroPage'));
const KilimanjaroRoutePage = React.lazy(() => import('./pages/KilimanjaroRoutePage'));
const KilimanjaroEquipmentsPage = React.lazy(() => import('./pages/KilimanjaroEquipmentsPage'));
const KilimanjaroPhotosPage = React.lazy(() => import('./pages/KilimanjaroPhotosPage'));
const PrivacyPage = React.lazy(() => import('./pages/PrivacyPage'));
const TermsPage = React.lazy(() => import('./pages/TermsPage'));
const NotFoundPage = React.lazy(() => import('./pages/NotFoundPage'));

/**
 * 英語版は /en 配下で配信する。basename を渡すことで useLocation() も <Link to> も
 * 日本語側と同じ論理パス（/kilimanjaro）のまま扱える。言語の切替は Router の外へ
 * 出るのでフルロードになり、プリレンダー済みの正しい HTML が読み込まれる。
 */
const ROUTER_BASENAME =
  typeof window !== 'undefined' && detectLangFromPath(window.location.pathname) === 'en' ? '/en' : '/';

const MEDIA_SELECTOR = 'img, video';

const hardenMediaElements = (root: ParentNode = document): void => {
  const mediaElements = root.querySelectorAll<HTMLElement>(MEDIA_SELECTOR);

  mediaElements.forEach((mediaElement) => {
    mediaElement.setAttribute('data-protected-media', 'true');
    mediaElement.setAttribute('draggable', 'false');
    mediaElement.classList.add('protected-media');

    if (mediaElement instanceof HTMLVideoElement) {
      mediaElement.setAttribute('controlsList', 'nodownload noremoteplayback');
      mediaElement.setAttribute('disablePictureInPicture', 'true');
    }
  });
};

const isEditableTarget = (target: EventTarget | null): boolean => {
  if (!(target instanceof HTMLElement)) {
    return false;
  }

  if (target.isContentEditable) {
    return true;
  }

  return ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName);
};

const shouldProtectMedia = (target: EventTarget | null): boolean => {
  if (!(target instanceof Element)) {
    return false;
  }

  return Boolean(target.closest(`${MEDIA_SELECTOR}, [data-protected-media="true"]`));
};

const Layout = ({ children }: { children: React.ReactNode }) => {
  const location = useLocation();

  useEffect(() => {
    // ページビュー送信。basename を除いた値では日英を区別できないので実 URL を送る
    pageview(window.location.pathname);
  }, [location]);

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <Header />
      <div className={`flex-grow ${location.pathname === '/' ? 'overflow-x-clip' : 'pt-16'}`}>{children}</div>
      <Footer />
    </div>
  );
};

function App() {
  useEffect(() => {
    // アプリ起動時にAI経由チェック
    trackAIVisit();
  }, []);

  useEffect(() => {
    hardenMediaElements();

    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        mutation.addedNodes.forEach((node) => {
          if (!(node instanceof Element)) {
            return;
          }

          if (node.matches(MEDIA_SELECTOR)) {
            hardenMediaElements(node.parentNode ?? document);
            return;
          }

          hardenMediaElements(node);
        });
      }
    });

    observer.observe(document.body, { childList: true, subtree: true });

    const handleContextMenu = (event: MouseEvent) => {
      if (shouldProtectMedia(event.target)) {
        event.preventDefault();
      }
    };

    const handleDragStart = (event: DragEvent) => {
      if (shouldProtectMedia(event.target)) {
        event.preventDefault();
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (isEditableTarget(event.target)) {
        return;
      }

      const lowerKey = event.key.toLowerCase();
      const isSaveOrPrint = (event.metaKey || event.ctrlKey) && (lowerKey === 's' || lowerKey === 'p');
      const isViewSource = (event.metaKey || event.ctrlKey) && lowerKey === 'u';
      const isDevTools = event.key === 'F12';

      if (isSaveOrPrint || isViewSource || isDevTools) {
        event.preventDefault();
      }
    };

    document.addEventListener('contextmenu', handleContextMenu);
    document.addEventListener('dragstart', handleDragStart);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      observer.disconnect();
      document.removeEventListener('contextmenu', handleContextMenu);
      document.removeEventListener('dragstart', handleDragStart);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  return (
    <Router basename={ROUTER_BASENAME}>
      <Layout>
        <Suspense fallback={<div className="flex justify-center items-center min-h-[50vh]"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500"></div></div>}>
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/profile" element={<ProfilePage />} />
            <Route path="/project" element={<ProjectPage />} />
            <Route path="/race" element={<RacePage />} />
            <Route path="/race/2026" element={<Race2026Page />} />
            <Route path="/cost" element={<CostPage />} />
            <Route path="/kilimanjaro" element={<KilimanjaroPage />} />
            <Route path="/kilimanjaro/route" element={<KilimanjaroRoutePage />} />
            <Route path="/kilimanjaro/equipments" element={<KilimanjaroEquipmentsPage />} />
            <Route path="/kilimanjaro/photos" element={<KilimanjaroPhotosPage />} />
            <Route path="/equipments" element={<EquipmentsPage />} />
            <Route path="/photos" element={<PhotosPage />} />
            <Route path="/equipment-guide" element={<EquipmentGuidePage />} />
            <Route path="/support" element={<SupportPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/links" element={<Links />} />
            <Route path="/app" element={<AppLandingPage />} />
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="/terms" element={<TermsPage />} />
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </Suspense>
      </Layout>
    </Router>
  );
}

export default App;
